import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

// @mui material components
import Card from '@mui/material/Card';
import Icon from '@mui/material/Icon';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import Pagination from '@mui/material/Pagination';
import Grid from '@mui/material/Grid';
import InputAdornment from '@mui/material/InputAdornment';
import Switch from '@mui/material/Switch';
import FormControlLabel from '@mui/material/FormControlLabel';

// Material Dashboard 2 PRO React components
import MDBox from 'components/MDBox';
import MDButton from 'components/MDButton';
import MDInput from 'components/MDInput';
import TableHeader from 'components/Table/TableHeader';

// Material Dashboard 2 PRO React examples
import Navbar from 'examples/Navbars/NavBar';
import { APP_STATE } from 'services/Define';
import { FetchData } from 'services/Request';
import * as Func from 'services/Function';

const initFind = {
  search: '',
  order_by: '',
  order_sort: 'asc',
  page: 1,
  perPage: 10,
  split_branch: false,
};

function RpProductRank() {
  const mod = 'rp_product_rank';
  const app = useSelector((state) => state[APP_STATE]);
  const { t, i18n } = useTranslation();
  const [pageTotal, setPageTotal] = useState(0);
  const [pageNo, setPageNo] = useState(1);
  const [record, setRecord] = useState([]);
  const [show, setShow] = useState(null);
  const [find, setFind] = useState(initFind);
  const [loading, setLoading] = useState(false);
  const [order, setOrder] = useState(initFind.order_sort);
  const [orderBy, setOrderBy] = useState(initFind.order_by);
  const [urlExcel, setUrlExcel] = useState('');

  const openShow = (e) => setShow(e.currentTarget);
  const closeShow = () => setShow(null);

  const initHeader = [
    { id: 'image', label: 'รูปสินค้า', align: 'left', sort: false },
    { id: 'code', label: 'SKU', align: 'left' },
    { id: 'name', label: 'ชื่อสินค้า', align: 'left' },
    ...(find.split_branch ? [{ id: 'branch_name', label: 'สาขา', align: 'left' }] : []),
    { id: 'cumulative_percent', label: 'Cumulative %', align: 'right' },
    { id: 'abc', label: 'ABC', align: 'center' },
    { id: 'cat_name', label: 'หมวดหมู่', align: 'left' },
    { id: 'cumulative_percent_cat', label: 'Cumulative % (หมวด)', align: 'right' },
    { id: 'abc_cat', label: 'ABC (หมวด)', align: 'center' },
  ];

  const view = (data) => {
    setFind(data);
    setLoading(true);
    closeShow();

    FetchData('POST', `/${mod}`, app.token, {
      lang: app.lang,
      search: data.search,
      split_branch: data.split_branch ? 'Y' : 'N',
      page: data.page,
      per_page: data.perPage,
      orderby: data.order_by,
      ordersort: data.order_sort,
    }).then((result) => {
      if (result.status === 'ok') {
        setRecord(result.data);
        setPageTotal(result.total_pages);
        setPageNo(result.page);
      }
      setLoading(false);
    });
  };

  const excel = () => {
    const params = new URLSearchParams({
      search: find.search,
      split_branch: find.split_branch ? 'Y' : 'N',
    });
    window.open(`${urlExcel}/download?${params.toString()}`, '_self');
  };

  const renderLoading = () => (
    <Backdrop sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }} open={loading}>
      <CircularProgress color='inherit' />
    </Backdrop>
  );

  const renderShow = (
    <Menu
      anchorEl={show}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      open={Boolean(show)}
      onClose={closeShow}
      keepMounted
    >
      {[10, 20, 100].map((num) => (
        <MenuItem key={num} onClick={() => view({ ...find, page: 1, perPage: num })}>
          {t(`element.show_record`, { num })}
        </MenuItem>
      ))}
    </Menu>
  );

  const handleSort = (e, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);

    view({ ...find, order_by: property, order_sort: isAsc ? 'desc' : 'asc' });
  };

  const init = () => {
    FetchData('GET', `/${mod}`, app.token, {
      lang: app.lang,
    }).then((result) => {
      if (result.status === 'ok') {
        setUrlExcel(result.excel);
        view(initFind);
      } else {
        Func.exitApp();
      }
    });
  };

  const renderSearch = () => (
    <Card sx={{ p: 2 }}>
      <Grid container spacing={2} alignItems='center'>
        <Grid item xs={12} sm={6} md={6} lg={4}>
          <MDBox
            noValidate
            component='form'
            role='form'
            onSubmit={(e) => {
              e.preventDefault();
              view({ ...find, page: 1 });
            }}
          >
            <MDInput
              name='searchform'
              fullWidth
              value={find.search}
              type='search'
              onChange={(e) => setFind({ ...find, search: e.target.value, page: 1 })}
              InputProps={{
                startAdornment: (
                  <InputAdornment position='start'>{t('element.search')} :</InputAdornment>
                ),
                autoComplete: 'off',
              }}
            />
          </MDBox>
        </Grid>
        <Grid item xs={12} sm={6} md={6} lg={2}>
          <FormControlLabel
            control={
              <Switch
                checked={find.split_branch}
                onChange={(e) => view({ ...find, split_branch: e.target.checked, page: 1 })}
                color='info'
              />
            }
            label='แยกสาขา'
          />
        </Grid>
        {record.length !== 0 && (
          <Grid item xs={12} sm={12} md={12} lg={1}>
            <MDButton variant='gradient' color='success' fullWidth onClick={excel}>
              <Icon>table_view</Icon>&nbsp;Excel
            </MDButton>
          </Grid>
        )}
      </Grid>
    </Card>
  );

  const renderList = () => (
    <MDBox mb={7}>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={3} md={2} lg={1}>
          <MDButton
            variant='contained'
            color='dark'
            size='large'
            fullWidth
            sx={{ pt: 1.6, pb: 1.6 }}
            href='/report_power_bi'
          >
            <Icon>content_paste_search</Icon>
          </MDButton>
        </Grid>
        <Grid item xs={12} sm={9} md={10} lg={11}>
          {renderSearch()}
        </Grid>
        <Grid item xs={12}>
          <TableContainer component={Paper} sx={{ minHeight: 400 }}>
            <Table aria-label='datatable table'>
              <TableHeader
                order={order}
                orderBy={orderBy}
                onRequestSort={handleSort}
                data={initHeader}
              />
              <TableBody>
                {record.length === 0 && !loading ? (
                  <TableRow>
                    <TableCell colSpan={initHeader.length} align='center'>
                      ไม่พบข้อมูล
                    </TableCell>
                  </TableRow>
                ) : null}
                {record.map((row) => (
                  <TableRow key={`${row.id}-${row.branch_name}`} hover>
                    <TableCell>{Func.imageDisplay(row)}</TableCell>
                    <TableCell>{row.code}</TableCell>
                    <TableCell>
                      <MDBox className='cut' width={250}>
                        {row.name}
                      </MDBox>
                    </TableCell>
                    {find.split_branch ? <TableCell>{row.branch_name}</TableCell> : null}
                    <TableCell align='right'>
                      {Func.numberDisplay(row.cumulative_percent, 2)}
                    </TableCell>
                    <TableCell align='center'>{row.abc}</TableCell>
                    <TableCell>{row.cat_name}</TableCell>
                    <TableCell align='right'>
                      {Func.numberDisplay(row.cumulative_percent_cat, 2)}
                    </TableCell>
                    <TableCell align='center'>{row.abc_cat}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>
        <Grid item xs={12}>
          {record.length === 0 ? null : (
            <Card sx={{ p: 2 }}>
              <MDBox display='flex' justifyContent='space-between' alignItems='flex-start'>
                <MDButton variant={show ? 'contained' : 'outlined'} color='dark' onClick={openShow}>
                  {t(`element.show_record`, { num: find.perPage })}&nbsp;
                  <Icon>keyboard_arrow_down</Icon>
                </MDButton>
                {renderShow}
                <Pagination
                  count={pageTotal}
                  variant='outlined'
                  color='secondary'
                  showFirstButton
                  showLastButton
                  page={pageNo}
                  onChange={(e, p) => view({ ...find, page: p })}
                />
              </MDBox>
            </Card>
          )}
        </Grid>
      </Grid>
    </MDBox>
  );

  useEffect(() => {
    i18n.changeLanguage(app.lang);
    init();
  }, []);

  return (
    <MDBox>
      <Navbar route={[t(`menu.report_power_bi`), t(`menu.rp_product_rank`)]} />
      {renderList()}
      {renderLoading()}
    </MDBox>
  );
}

export default RpProductRank;
